const isObject = (value) => (
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value)
);

const firstDefined = (...values) => values.find(
  value => value !== undefined && value !== null,
);

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const toIdentifier = (value) => (
  value === undefined || value === null || value === '' ? '' : String(value)
);

const ORDER_TYPE_NAMES = [
  'BUY',
  'SELL',
  'BUY_LIMIT',
  'SELL_LIMIT',
  'BUY_STOP',
  'SELL_STOP',
  'BUY_STOP_LIMIT',
  'SELL_STOP_LIMIT',
  'CLOSE_BY',
];

const ORDER_TYPE_CODES = ORDER_TYPE_NAMES.reduce((result, name, index) => {
  result[name] = index;
  return result;
}, {});

const ENTRY_TYPE_CODES = {
  IN: 0,
  OUT: 1,
  INOUT: 2,
  OUT_BY: 3,
};

const normalizeOrderTypeName = (value, fallback = 'BUY') => {
  if (typeof value === 'string' && Number.isNaN(Number(value))) {
    const normalized = value.toUpperCase();
    return Object.prototype.hasOwnProperty.call(ORDER_TYPE_CODES, normalized)
      ? normalized
      : fallback;
  }

  const code = Number(value);
  return Number.isInteger(code) && ORDER_TYPE_NAMES[code]
    ? ORDER_TYPE_NAMES[code]
    : fallback;
};

const normalizeOrderTypeCode = (value, fallback = 0) => {
  if (typeof value === 'string' && Number.isNaN(Number(value))) {
    return ORDER_TYPE_CODES[value.toUpperCase()] ?? fallback;
  }

  const code = Number(value);
  return Number.isInteger(code) ? code : fallback;
};

const inferMarket = (symbol) => {
  const path = String(symbol?.path || symbol?.category || '').toLowerCase();
  const name = String(symbol?.symbol || symbol?.name || '').toUpperCase();

  if (path.includes('crypto')) return 'Crypto';
  if (path.includes('stock')) return 'Stocks';
  if (path.includes('indice') || path.includes('index')) return 'Indices';
  if (path.includes('commod')) return 'Commodities';
  if (path.includes('forex') || /^[A-Z]{6}$/.test(name)) return 'Forex';
  return 'Forex';
};

export const isSuccessResponse = (payload) => {
  if (payload === null || payload === undefined) return false;
  if (Array.isArray(payload)) return true;
  if (typeof payload !== 'object') return Boolean(payload);

  if (payload.status === false || payload.success === false || payload.ok === false) {
    return false;
  }
  if (payload.status === true || payload.success === true || payload.ok === true) {
    return true;
  }
  if (payload.error !== undefined && payload.error !== null && payload.error !== '') {
    return false;
  }

  return true;
};

export const getPayloadData = (payload) => {
  if (!isObject(payload)) return payload;

  const hasData = Object.prototype.hasOwnProperty.call(payload, 'data');
  const looksLikeEnvelope = (
    Object.prototype.hasOwnProperty.call(payload, 'status') ||
    Object.prototype.hasOwnProperty.call(payload, 'success') ||
    Object.prototype.hasOwnProperty.call(payload, 'message') ||
    Object.prototype.hasOwnProperty.call(payload, 'event')
  );
  const dataLooksLikeCollection = (
    Array.isArray(payload.data) ||
    Array.isArray(payload.data?.items) ||
    Array.isArray(payload.data?.results) ||
    Array.isArray(payload.data?.data)
  );

  return hasData && (
    looksLikeEnvelope ||
    dataLooksLikeCollection ||
    Object.keys(payload).length === 1
  )
    ? payload.data
    : payload;
};

export const getPayloadItems = (payload) => {
  if (Array.isArray(payload)) return payload;

  const data = getPayloadData(payload);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;

  return [];
};

export const normalizeEnvelope = (payload, normalizedData) => {
  const source = isObject(payload) ? payload : {};
  const success = isSuccessResponse(payload);
  const data = normalizedData !== undefined
    ? normalizedData
    : getPayloadData(payload);

  return {
    ...source,
    status: success,
    success,
    message: source.message || (success ? 'Success' : 'Request failed'),
    data,
  };
};

export const normalizeItemsEnvelope = (payload, itemNormalizer = value => value) => {
  const originalData = getPayloadData(payload);
  const items = getPayloadItems(payload).map(itemNormalizer);
  const data = isObject(originalData)
    ? { ...originalData, items }
    : { items };

  return {
    ...normalizeEnvelope(payload, data),
    items,
  };
};

export const normalizeSymbol = (value = {}) => {
  const symbol = isObject(value) ? value : { symbol: value };
  const name = String(firstDefined(symbol.symbol, symbol.name, symbol.code, '') || '');
  const clean = name.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const inferredBase = clean.length >= 6 ? clean.slice(0, 3) : '';
  const inferredQuote = clean.length >= 6 ? clean.slice(3, 6) : '';
  const base = String(firstDefined(
    symbol.base,
    symbol.currency_base,
    symbol.currencyBase,
    inferredBase,
  ) || '');
  const quote = String(firstDefined(
    symbol.quote,
    symbol.currency_profit,
    symbol.currencyProfit,
    inferredQuote,
  ) || '');
  const market = symbol.market || inferMarket(symbol);
  let contractSize = toNumber(firstDefined(
    symbol.contract_size,
    symbol.contractSize,
    symbol.trade_contract_size,
  ));
  if (clean.includes('XAU') || clean.includes('GOLD')) {
    contractSize = 100;
  } else if (clean.includes('XAG') || clean.includes('SILVER')) {
    contractSize = 5000;
  } else if (clean.length >= 6 && /^(EUR|GBP|AUD|NZD|USD|CAD|CHF|JPY|SGD|HKD|CNH|SEK|NOK|ZAR|TRY|MXN|PLN)/i.test(clean)) {
    contractSize = 100000;
  }
  const tickSize = toNumber(firstDefined(symbol.tick_size, symbol.tickSize));
  const tickValue = toNumber(firstDefined(symbol.tick_value, symbol.tickValue));
  const volumeMin = toNumber(firstDefined(symbol.volume_min, symbol.volumeMin), 0.01);
  const volumeMax = toNumber(firstDefined(symbol.volume_max, symbol.volumeMax), 100);
  const volumeStep = toNumber(firstDefined(symbol.volume_step, symbol.volumeStep), 0.01);

  return {
    ...symbol,
    symbol: name,
    name: symbol.name || name,
    description: symbol.description || name,
    digits: toNumber(symbol.digits, 5),
    base,
    quote,
    currency_base: base,
    currencyBase: base,
    currency_profit: quote,
    currencyProfit: quote,
    market,
    category: symbol.category || market,
    contract_size: contractSize,
    contractSize,
    tick_size: tickSize,
    tickSize,
    tick_value: tickValue,
    tickValue,
    volume_min: volumeMin,
    volumeMin,
    volume_max: volumeMax,
    volumeMax,
    volume_step: volumeStep,
    volumeStep,
    ...(symbol.bid !== undefined ? { bid: toNumber(symbol.bid) } : {}),
    ...(symbol.ask !== undefined ? { ask: toNumber(symbol.ask) } : {}),
  };
};

export const normalizePrice = (value = {}) => {
  const price = isObject(value) ? value : {};
  const timeMsc = toNumber(firstDefined(price.time_msc, price.timeMsc));
  const rawTime = toNumber(firstDefined(price.time, price.datetime, price.timestamp));
  const timestamp = timeMsc || (
    rawTime > 0 && rawTime < 100000000000 ? rawTime * 1000 : rawTime
  );
  const time = price.time !== undefined
    ? toNumber(price.time)
    : (timestamp > 100000000000 ? Math.floor(timestamp / 1000) : timestamp);
  const volumeExt = toNumber(firstDefined(price.volume_ext, price.volumeExt, price.volume));

  return {
    ...price,
    symbol: String(price.symbol || ''),
    bid: toNumber(price.bid),
    ask: toNumber(price.ask),
    last: toNumber(price.last),
    volume: toNumber(price.volume),
    volume_ext: volumeExt,
    volumeExt,
    time,
    time_msc: timeMsc || timestamp,
    timeMsc: timeMsc || timestamp,
    timestamp,
  };
};

export const normalizeOrder = (value = {}) => {
  const order = isObject(value) ? value : {};
  const ticket = toIdentifier(firstDefined(order.ticket, order.order, order.id, order._id));
  const orderId = toIdentifier(firstDefined(order.order, ticket));
  const positionId = toIdentifier(firstDefined(
    order.position_id,
    order.positionID,
    order.positionId,
    order.position,
  ));
  const rawType = firstDefined(order.order_type, order.orderType, order.type, order.action);
  const orderType = normalizeOrderTypeName(rawType);
  const type = normalizeOrderTypeCode(rawType);
  const volume = toNumber(firstDefined(order.volume, order.volume_initial, order.volumeInitial));
  const volumeCurrent = toNumber(firstDefined(
    order.volume_current,
    order.volumeCurrent,
    volume,
  ));
  const price = toNumber(firstDefined(
    order.price,
    order.price_order,
    order.priceOrder,
    order.open_price,
    order.openPrice,
    order.priceOpen,
  ));
  const priceCurrent = toNumber(firstDefined(
    order.price_current,
    order.priceCurrent,
    order.current_price,
    order.currentPrice,
    price,
  ));
  const timeSetup = toNumber(firstDefined(order.time_setup, order.timeSetup));
  const timeDone = toNumber(firstDefined(order.time_done, order.timeDone));

  return {
    ...order,
    ticket,
    order: orderId,
    id: firstDefined(order.id, orderId),
    login: toIdentifier(order.login),
    order_type: orderType,
    orderType,
    type,
    action: type,
    side: order.side || (type % 2 === 0 ? 'BUY' : 'SELL'),
    volume,
    volume_initial: volume,
    volumeInitial: volume,
    volume_current: volumeCurrent,
    volumeCurrent,
    price,
    price_order: price,
    priceOrder: price,
    open_price: price,
    openPrice: price,
    priceOpen: price,
    price_current: priceCurrent,
    priceCurrent,
    current_price: priceCurrent,
    currentPrice: priceCurrent,
    sl: toNumber(firstDefined(order.sl, order.stopLoss)),
    tp: toNumber(firstDefined(order.tp, order.takeProfit)),
    position_id: positionId,
    positionId,
    positionID: positionId,
    time_setup: timeSetup,
    timeSetup,
    time_done: timeDone,
    timeDone,
  };
};

export const normalizePosition = (value = {}) => {
  const position = isObject(value) ? value : {};
  const ticket = toIdentifier(firstDefined(
    position.ticket,
    position.position,
    position.position_id,
    position.positionID,
    position.id,
  ));
  const positionId = toIdentifier(firstDefined(
    position.position_id,
    position.positionID,
    position.positionId,
    position.position,
    ticket,
  ));
  const side = String(firstDefined(
    position.side,
    position.order_type,
    position.orderType,
    position.type,
    'BUY',
  )).toUpperCase();
  const type = normalizeOrderTypeCode(side === 'SELL' ? 'SELL' : side);
  const openPrice = toNumber(firstDefined(
    position.open_price,
    position.openPrice,
    position.priceOpen,
    position.price,
  ));
  const currentPrice = toNumber(firstDefined(
    position.price_current,
    position.priceCurrent,
    position.current_price,
    position.currentPrice,
    openPrice,
  ));
  const timeCreate = toNumber(firstDefined(position.time_create, position.timeCreate));
  const timeUpdate = toNumber(firstDefined(position.time_update, position.timeUpdate));
  const swap = toNumber(firstDefined(position.swap, position.storage));

  return {
    ...position,
    ticket,
    id: firstDefined(position.id, ticket),
    position: positionId,
    position_id: positionId,
    positionId,
    positionID: positionId,
    login: toIdentifier(position.login),
    side: type === 1 ? 'SELL' : 'BUY',
    order_type: type === 1 ? 'SELL' : 'BUY',
    orderType: type === 1 ? 'SELL' : 'BUY',
    type,
    action: type,
    volume: toNumber(position.volume),
    open_price: openPrice,
    openPrice,
    priceOpen: openPrice,
    price_current: currentPrice,
    priceCurrent: currentPrice,
    current_price: currentPrice,
    currentPrice,
    sl: toNumber(firstDefined(position.sl, position.stopLoss)),
    tp: toNumber(firstDefined(position.tp, position.takeProfit)),
    profit: toNumber(position.profit),
    swap,
    storage: swap,
    time_create: timeCreate,
    timeCreate,
    time_update: timeUpdate,
    timeUpdate,
  };
};

export const normalizeDeal = (value = {}) => {
  const deal = isObject(value) ? value : {};
  const dealId = toIdentifier(firstDefined(deal.deal, deal.ticket, deal.id));
  const orderId = toIdentifier(deal.order);
  const positionId = toIdentifier(firstDefined(
    deal.position_id,
    deal.positionID,
    deal.positionId,
    deal.position,
  ));
  const rawSide = String(firstDefined(
    deal.side,
    deal.order_type,
    deal.type,
    deal.action,
    'OTHER',
  )).toUpperCase();
  const numericType = Number(firstDefined(deal.type, deal.action));
  const type = rawSide === 'BUY'
    ? 0
    : rawSide === 'SELL'
      ? 1
      : Number.isFinite(numericType)
        ? numericType
        : 2;
  const side = type === 0 ? 'BUY' : type === 1 ? 'SELL' : 'OTHER';
  const entryType = String(firstDefined(deal.entry_type, deal.entryType, '')).toUpperCase();
  const entry = Number.isFinite(Number(deal.entry))
    ? Number(deal.entry)
    : (ENTRY_TYPE_CODES[entryType] ?? null);
  const time = toNumber(firstDefined(deal.time, deal.time_msc, deal.timeMsc));
  const timeMsc = toNumber(firstDefined(
    deal.time_msc,
    deal.timeMsc,
    time > 0 && time < 100000000000 ? time * 1000 : time,
  ));
  const swap = toNumber(firstDefined(deal.swap, deal.storage));

  return {
    ...deal,
    deal: dealId,
    ticket: toIdentifier(firstDefined(deal.ticket, dealId)),
    id: firstDefined(deal.id, dealId),
    order: orderId,
    position: positionId,
    position_id: positionId,
    positionId,
    positionID: positionId,
    login: toIdentifier(deal.login),
    side,
    order_type: side,
    type,
    action: type,
    entry_type: entryType,
    entryType,
    entry,
    volume: toNumber(deal.volume),
    price: toNumber(deal.price),
    priceCurrent: toNumber(deal.price),
    profit: toNumber(deal.profit),
    commission: toNumber(deal.commission),
    swap,
    storage: swap,
    time,
    time_msc: timeMsc,
    timeMsc,
    timestamp: timeMsc,
  };
};

export const isClosedTradeHistoryDeal = (value = {}) => {
  const entryType = String(
    firstDefined(value.entry_type, value.entryType, ''),
  ).trim().toUpperCase();
  const side = String(
    firstDefined(value.side, value.order_type, value.orderType, ''),
  ).trim().toUpperCase();

  return entryType === 'OUT' && Boolean(side) && side !== 'OTHER';
};

export const isTradeHistoryDeal = (value = {}) => {
  const dealId = firstDefined(value.deal, value.ticket, value.id);
  const entryType = String(
    firstDefined(value.entry_type, value.entryType, ''),
  ).trim().toUpperCase();
  const side = String(
    firstDefined(value.side, value.order_type, value.orderType, ''),
  ).trim().toUpperCase();
  const positionId = firstDefined(
    value.position_id,
    value.positionID,
    value.positionId,
    value.position,
  );

  return Number(dealId) > 0 &&
    Boolean(value.symbol) &&
    Number(positionId) > 0 &&
    (side === 'BUY' || side === 'SELL') &&
    (entryType === 'IN' || entryType === 'OUT');
};

export const isBalanceHistoryDeal = (value = {}) => {
  const dealId = firstDefined(value.deal, value.ticket, value.id);
  const side = String(
    firstDefined(value.side, value.order_type, value.orderType, ''),
  ).trim().toUpperCase();
  const symbol = String(value.symbol || '').trim();
  const comment = String(value.comment || '').trim();
  const amount = Number(firstDefined(value.profit, value.value));

  return Number(dealId) > 0 &&
    !symbol &&
    (!side || side === 'OTHER') &&
    (Boolean(comment) || (Number.isFinite(amount) && amount !== 0));
};

export const isOrderHistoryDeal = (value = {}) => (
  isTradeHistoryDeal(value) || isBalanceHistoryDeal(value)
);

export const normalizeAccount = (value = {}, fallbackUser = {}) => {
  const account = isObject(value) ? value : {};
  const user = isObject(fallbackUser) ? fallbackUser : {};
  const login = toIdentifier(firstDefined(account.login, user.login));
  const balance = toNumber(firstDefined(account.balance, user.balance));
  const credit = toNumber(firstDefined(account.credit, user.credit));
  const floatingProfit = toNumber(firstDefined(
    account.floating_profit,
    account.floatingProfit,
    account.profit,
  ));
  const equity = toNumber(
    firstDefined(account.equity),
    balance + credit + floatingProfit,
  );
  const margin = toNumber(account.margin);
  const marginFree = toNumber(
    firstDefined(account.margin_free, account.marginFree, account.freeMargin),
    equity - margin,
  );
  const marginLevel = toNumber(firstDefined(account.margin_level, account.marginLevel));
  const leverage = toNumber(firstDefined(
    account.leverage,
    account.marginLeverage,
    user.leverage,
  ), 100);

  const group = String(firstDefined(
    account.group,
    user.group,
    user.group_id?.group_name,
    user.groupId?.groupName,
    '',
  ) || '');
  const isCent = /cent/i.test(group) || /USC|EUX|GBX/i.test(account.currency || user.currency || '');
  const currency = String(firstDefined(account.currency, user.currency, isCent ? 'USC' : 'USD')).toUpperCase();

  return {
    ...account,
    login,
    balance,
    credit,
    equity,
    margin,
    margin_free: marginFree,
    marginFree,
    freeMargin: marginFree,
    margin_level: marginLevel,
    marginLevel,
    floating_profit: floatingProfit,
    floatingProfit,
    profit: floatingProfit,
    leverage,
    marginLeverage: leverage,
    currency,
    is_cent: isCent,
    isCent,
  };
};

export const normalizeUser = (value = {}, fallbackAccount = {}) => {
  const user = isObject(value) ? value : {};
  const account = isObject(fallbackAccount) ? fallbackAccount : {};
  const login = toIdentifier(firstDefined(user.login, account.login));
  const group = String(firstDefined(
    user.group,
    user.group_id?.group_name,
    user.groupId?.groupName,
    '',
  ) || '');
  const email = String(firstDefined(user.email, user.eMail, '') || '');
  const firstName = String(firstDefined(user.firstName, user.first_name, '') || '');
  const lastName = String(firstDefined(user.lastName, user.last_name, '') || '');
  const name = user.name || [firstName, lastName].filter(Boolean).join(' ') || login;
  const isCent = /cent/i.test(group) || /USC|EUX|GBX/i.test(user.currency || account.currency || '');
  const currency = String(firstDefined(user.currency, account.currency, isCent ? 'USC' : 'USD')).toUpperCase();

  return {
    ...user,
    login,
    id: firstDefined(user.id, login),
    group,
    group_id: isObject(user.group_id)
      ? { ...user.group_id, group_name: user.group_id.group_name || group }
      : { group_name: group },
    groupId: isObject(user.groupId)
      ? { ...user.groupId, groupName: user.groupId.groupName || group }
      : { groupName: group },
    name,
    firstName,
    first_name: firstName,
    lastName,
    last_name: lastName,
    email,
    eMail: email,
    leverage: toNumber(firstDefined(user.leverage, account.leverage), 100),
    balance: toNumber(firstDefined(user.balance, account.balance)),
    credit: toNumber(firstDefined(user.credit, account.credit)),
    currency,
    is_cent: isCent,
    isCent,
  };
};
